/**
 * Safety slice — anchor watch, MOB, safety contour/depth, look-ahead settings,
 * plus the most recent dangers list so the UI can render alarms without
 * re-running the check on every render.
 */
import { create } from 'zustand';
import { loadJson, saveJson } from './persist';
import {
  DEFAULT_SAFETY,
  type AnchorWatch,
  type DangerHit,
  type MobMark,
  type SafetySettings,
} from '@/safety';

const SAFETY_LS = 'acp.safety.settings.v1';

interface SafetyState {
  anchor: AnchorWatch | null;
  setAnchor: (a: AnchorWatch | null) => void;
  mob: MobMark | null;
  setMob: (m: MobMark | null) => void;
  settings: SafetySettings;
  setSettings: (patch: Partial<SafetySettings>) => void;
  dangers: DangerHit[];
  setDangers: (d: DangerHit[]) => void;
}

export const useSafetyStore = create<SafetyState>((set) => ({
  anchor: null,
  setAnchor: (anchor) => set({ anchor }),
  mob: null,
  setMob: (mob) => set({ mob }),
  // The look-ahead cone (on/off, time vs distance, its reach and half-angle) and
  // the depth thresholds must survive a kill/restart, so a skipper who set the
  // cone doesn't lose it next launch. Merged over defaults so a profile saved by
  // an older build still gets any newer field's default.
  settings: loadJson<SafetySettings>(SAFETY_LS, DEFAULT_SAFETY),
  setSettings: (patch) =>
    set((s) => {
      const settings = { ...s.settings, ...patch };
      saveJson(SAFETY_LS, settings);
      return { settings };
    }),
  dangers: [],
  setDangers: (dangers) => set({ dangers }),
}));
